import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useT } from '../../i18n/index.ts';
import { shanghaiLine, tokyoLine, tokyoSecondsLeft, type RowWriteStatus as Status } from './writeStatus.ts';

/** Re-render once a second while a Tokyo countdown is running. */
function useCountdownNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [active]);
  return active ? now : Date.now();
}

/**
 * One row's write state, inline next to the row (#2658): 「正在写入：…」 while
 * the request is out, then 「已保存」 with where it is in effect, or the
 * server's error text verbatim.
 */
export function RowWriteStatus({
  status,
  testId,
}: {
  readonly status: Status | undefined;
  readonly testId: string;
}): JSX.Element | null {
  const t = useT();
  const counting = status?.phase === 'saved' && (tokyoSecondsLeft(status, Date.now()) ?? 0) > 0;
  const now = useCountdownNow(counting);
  if (status === undefined) return null;
  if (status.phase === 'saving') {
    return (
      <div role="status" aria-live="polite" className="flex items-center gap-1.5 text-label text-signal" data-testid={testId}>
        <Loader2 aria-hidden="true" className="h-3.5 w-3.5 shrink-0 animate-spin" />
        <span>{t('guaranteed.write.saving', { what: status.label })}</span>
      </div>
    );
  }
  if (status.phase === 'error') {
    return (
      <div role="alert" className="text-label text-bad" data-testid={testId}>
        {t('guaranteed.write.failed', { message: status.message })}
      </div>
    );
  }
  const waiting = status.cn.kind === 'waiting';
  return (
    <div role="status" aria-live="polite" className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-label text-paper-mid" data-testid={testId}>
      <span className="font-medium text-ok">
        {status.op === 'delete' ? t('guaranteed.write.deleted') : t('guaranteed.write.saved')}
      </span>
      <span data-testid={`${testId}-jp`}>{tokyoLine(t, status, now)}</span>
      <span
        className={`inline-flex items-center gap-1 ${status.cn.kind === 'timeout' ? 'text-warn' : ''}`}
        data-testid={`${testId}-cn`}
      >
        {waiting ? <Loader2 aria-hidden="true" className="h-3 w-3 shrink-0 animate-spin" /> : null}
        {shanghaiLine(t, status)}
      </span>
    </div>
  );
}
